import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";

const LiveCtx = createContext(null);

/** Live WebSocket + shared dashboard state (agents, diagnostics, commands, tasks). */
export function LiveProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const [agents, setAgents] = useState([]);
  const [diag, setDiag] = useState([]);
  const [commands, setCommands] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [routineRun, setRoutineRun] = useState(null);
  const wsRef = useRef(null);

  const refreshAgents = useCallback(() => api.get("/agents").then((r) => setAgents(r.data)).catch(() => {}), []);
  const refreshDiag = useCallback(() => api.get("/diagnostics").then((r) => setDiag(r.data.items)).catch(() => {}), []);
  const refreshTasks = useCallback(() => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {}), []);
  const refreshCommands = useCallback(() => api.get("/commands?limit=30").then((r) => setCommands(r.data)).catch(() => {}), []);

  useEffect(() => {
    refreshAgents(); refreshDiag(); refreshTasks(); refreshCommands();
    let delay = 1000, stop = false, timer;
    const connect = () => {
      const ws = new WebSocket((BACKEND_URL || window.location.origin).replace(/^http/, "ws") + "/api/ws");
      wsRef.current = ws;
      ws.onopen = () => { setConnected(true); delay = 1000; refreshAgents(); refreshDiag(); refreshTasks(); refreshCommands(); };
      ws.onclose = () => {
        setConnected(false);
        if (!stop) timer = setTimeout(connect, (delay = Math.min(delay * 2, 30000)));
      };
      ws.onmessage = (m) => {
        const ev = JSON.parse(m.data);
        if (ev.type === "command") {
          setCommands((cs) => [ev.command, ...cs.filter((c) => c.id !== ev.command.id)].slice(0, 30));
        } else if (ev.type === "agent" || ev.type === "agent_state") {
          refreshAgents(); refreshDiag();
          if (ev.type === "agent") toast(`Agente ${ev.status}`);
        } else if (ev.type === "notification") {
          const n = ev.notification;
          (n.level === "error" ? toast.error : n.level === "warning" ? toast.warning : toast)(n.title, { description: n.message });
          if ("Notification" in window && Notification.permission === "granted" && document.hidden) new Notification(n.title, { body: n.message });
          refreshTasks();
        } else if (ev.type === "routine_run") {
          setRoutineRun(ev.run);
        }
      };
    };
    connect();
    const iv = setInterval(() => { refreshAgents(); refreshDiag(); }, 30000);
    return () => { stop = true; clearTimeout(timer); clearInterval(iv); wsRef.current?.close(); };
  }, [refreshAgents, refreshDiag, refreshTasks, refreshCommands]);

  const status = (id) => !connected ? { status: "desconocido", detail: "Sin contacto con el servidor. El estado anterior puede estar desactualizado." } : diag.find((d) => d.id === id) || { status: "desconocido", detail: "" };
  const visibleAgents = connected ? agents : agents.map((a) => ({ ...a, online: false }));
  const onlineAgent = visibleAgents.find((a) => a.online);

  return (
    <LiveCtx.Provider value={{ connected, agents: visibleAgents, onlineAgent, diag, status, commands, tasks, routineRun, setRoutineRun,
      refreshAgents, refreshDiag, refreshTasks, refreshCommands }}>
      {children}
    </LiveCtx.Provider>
  );
}

export const useLive = () => useContext(LiveCtx);
